// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
// ==========================================================================

/**
 * Locale-aware loading of markdown documents served from /public.
 *
 * Translated copies live beside the English original with the language code
 * spliced in before the extension:
 *
 *   /changelog-squarebit-eye.md      <- English source of truth
 *   /changelog-squarebit-eye.de.md   <- German
 *   /changelog-squarebit-eye.zh.md   <- Chinese
 *
 * English always falls back to the base file. Any other language tries its own
 * file first and silently falls back to English when that file is missing,
 * empty, or unfetchable. That fallback is load-bearing: a release adds English
 * changelog entries before the translations land, and a reader on a language we
 * haven't caught up on must still see the release notes rather than an error.
 */

/** Language code with any region tag stripped ("de-DE" -> "de"). */
const baseLanguage = (lang?: string): string => (lang || 'en').split('-')[0];

/**
 * Path of the translated copy of `basePath` for `lang`, or `basePath` itself
 * for English. Exported for tests and for callers that want to preload.
 */
export const localizedMarkdownPath = (basePath: string, lang?: string): string => {
  const code = baseLanguage(lang);
  if (code === 'en') return basePath;
  return basePath.replace(/\.md$/i, `.${code}.md`);
};

/**
 * A single-page app serves index.html for unknown paths instead of a 404, so a
 * missing locale file comes back as `200 text/html`. Sniff the body: anything
 * that starts like an HTML document is the SPA shell, not a changelog.
 */
const isHtmlDocument = (text: string): boolean =>
  /^\s*(?:<!doctype\s+html|<html\b)/i.test(text);

/**
 * Append a cache-busting token to a markdown path.
 *
 * These files are served from /public at a stable URL, unlike the JS bundle
 * whose filename carries a content hash. A deploy therefore busts the bundle
 * and leaves the markdown alone, so a reader whose browser fetched a changelog
 * before a release keeps being shown the old one: the page reports the new
 * version while the notes for it are missing. Keying the URL to the version
 * makes each release a different URL, so the cache misses exactly when it
 * should and still works normally between releases.
 *
 * It is not sufficient on its own. A changelog is edited between releases too,
 * to correct an entry or to fold in the notes of a version that was withdrawn,
 * and those edits keep the same version and therefore the same URL - so a
 * reader who had already opened the page keeps the copy from before the edit.
 * That is why the fetch revalidates as well; see fetchMarkdown.
 */
const withCacheKey = (path: string, cacheKey?: string): string =>
  cacheKey ? `${path}?v=${encodeURIComponent(cacheKey)}` : path;

/** Fetch one markdown file; `null` for anything that isn't usable markdown. */
const fetchMarkdown = async (path: string): Promise<string | null> => {
  try {
    // 'no-cache' revalidates rather than refetching: the browser still sends
    // its validators and an unchanged file costs a 304, but an edited one is
    // picked up immediately. Without it, editing a changelog without bumping
    // the version leaves everyone who had already opened the page on the old
    // copy, because the version-keyed URL has not changed.
    const res = await fetch(path, { cache: 'no-cache' });
    if (!res.ok) return null;
    const text = await res.text();
    if (!text.trim() || isHtmlDocument(text)) return null;
    return text;
  } catch {
    return null;
  }
};

/**
 * Load `basePath` in the active language, falling back to the English original
 * whenever the translated file is absent or unreadable.
 *
 * Rejects only when the English original itself cannot be loaded.
 */
export const fetchLocalizedMarkdown = async (
  basePath: string,
  lang?: string,
  cacheKey?: string
): Promise<string> => {
  const localized = localizedMarkdownPath(basePath, lang);

  if (localized !== basePath) {
    const translated = await fetchMarkdown(withCacheKey(localized, cacheKey));
    if (translated !== null) return translated;
  }

  const english = await fetchMarkdown(withCacheKey(basePath, cacheKey));
  if (english !== null) return english;

  throw new Error(`Unable to load markdown document: ${basePath}`);
};
